import React, { useState, useMemo, useEffect } from 'react';
import {
  Package,
  Plus,
  Search,
  Filter,
  AlertTriangle,
  AlertCircle,
  CheckCircle2,
  XCircle,
  TrendingUp,
  ArrowDownRight,
  ArrowUpRight,
  FileSpreadsheet,
  FileText,
  Edit2,
  Trash2,
  RefreshCw,
  History,
  Layers,
  Boxes,
  MapPin,
  Truck,
  DollarSign,
  Tag,
  Barcode,
  SlidersHorizontal,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  X,
  PlusCircle,
  MinusCircle,
  ExternalLink,
} from 'lucide-react';
import { CompanyInfo, CurrencyCode, ExchangeRateInfo, InventoryCategory, InventoryItem, InventoryLot, StockMovement } from '../types';
import { defaultInventoryCategories } from '../data/initialData';
import {
  formatTL,
  formatUSD,
  formatDateTR,
  generateNextInventoryId,
  saveInventoryCategories,
  getStoredExchangeRate,
  saveStoredExchangeRate,
  fetchLiveExchangeRate,
  convertUSDToTL,
  convertTLToUSD,
  getItemPriceTL,
  getItemCostTL,
  calculateItemWeightedAvgCost,
  calculateItemTotalInventoryValue,
  addInventoryLotPurchase,
  consumeItemStockLowestCostFirst,
  getItemActiveLots,
  getLotCostTL,
  getSortedLotsByLowestCost,
} from '../utils/storage';
import { exportInventoryExcel } from '../utils/excelGenerator';
import { exportInventoryPDF } from '../utils/pdfGenerator';
import { ManageInventoryCategoriesModal } from './ManageInventoryCategoriesModal';

interface InventoryViewProps {
  inventory: InventoryItem[];
  categories?: string[];
  stockMovements: StockMovement[];
  onUpdateInventory: (items: InventoryItem[]) => void;
  onUpdateCategories?: (categories: string[]) => void;
  onUpdateStockMovements: (movements: StockMovement[]) => void;
  companyInfo: CompanyInfo;
  isTechnician?: boolean;
}

export const InventoryView: React.FC<InventoryViewProps> = ({
  inventory,
  categories = defaultInventoryCategories,
  stockMovements,
  onUpdateInventory,
  onUpdateCategories,
  onUpdateStockMovements,
  companyInfo,
  isTechnician = false,
}) => {
  // Filters & State
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [stockStatusFilter, setStockStatusFilter] = useState<'all' | 'critical' | 'out_of_stock' | 'in_stock'>('all');
  const [sortBy, setSortBy] = useState<'name_asc' | 'qty_asc' | 'qty_desc' | 'cost_desc' | 'date_desc'>('name_asc');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const pageSize = 20;

  // Modals state
  const [isItemModalOpen, setIsItemModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<InventoryItem | null>(null);
  const [isMovementModalOpen, setIsMovementModalOpen] = useState(false);
  const [movementTargetItem, setMovementTargetItem] = useState<InventoryItem | null>(null);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [historyFilterItemId, setHistoryFilterItemId] = useState<string | null>(null);
  const [isLotsModalOpen, setIsLotsModalOpen] = useState(false);
  const [lotsTargetItem, setLotsTargetItem] = useState<InventoryItem | null>(null);
  const [isCategoriesModalOpen, setIsCategoriesModalOpen] = useState(false);

  // Active View Tab inside Inventory: 'list' | 'movements'
  const [viewMode, setViewMode] = useState<'list' | 'movements'>('list');

  // Exchange Rate State (TCMB USD/TRY)
  const [exchangeRate, setExchangeRate] = useState<ExchangeRateInfo>(() => getStoredExchangeRate());
  const [isUpdatingRate, setIsUpdatingRate] = useState(false);

  const handleRefreshRate = async (force = true) => {
    setIsUpdatingRate(true);
    try {
      const freshRate = await fetchLiveExchangeRate(force);
      setExchangeRate(freshRate);
    } catch (err) {
      console.error('Exchange rate refresh error:', err);
    } finally {
      setIsUpdatingRate(false);
    }
  };

  useEffect(() => {
    // Check if exchange rate is older than 1 hour on mount
    const currentRate = getStoredExchangeRate();
    setExchangeRate(currentRate);
    const lastTime = currentRate?.updatedAt ? new Date(currentRate.updatedAt).getTime() : 0;
    if (!lastTime || Date.now() - lastTime >= 60 * 60 * 1000) {
      handleRefreshRate(true);
    }

    // Listen to custom update event from background sync
    const handleExchangeRateChange = (e: any) => {
      if (e.detail) {
        setExchangeRate(e.detail);
      } else {
        setExchangeRate(getStoredExchangeRate());
      }
    };

    // Hourly timer while inventory screen is open
    const hourlyTimer = setInterval(() => {
      handleRefreshRate(true);
    }, 60 * 60 * 1000);

    window.addEventListener('ts_exchange_rate_updated', handleExchangeRateChange);
    window.addEventListener('storage', handleExchangeRateChange);
    return () => {
      clearInterval(hourlyTimer);
      window.removeEventListener('ts_exchange_rate_updated', handleExchangeRateChange);
      window.removeEventListener('storage', handleExchangeRateChange);
    };
  }, []);

  // Calculate Metrics with Lot Batch Precision
  const metrics = useMemo(() => {
    const totalKinds = inventory.length;
    const totalUnits = inventory.reduce((sum, item) => sum + (item.quantity || 0), 0);
    const totalCost = inventory.reduce((sum, item) => sum + calculateItemTotalInventoryValue(item), 0);
    const totalSaleValue = inventory.reduce((sum, item) => sum + (item.quantity || 0) * (item.salePrice || 0), 0);
    const potentialProfit = totalSaleValue - totalCost;

    const criticalItems = inventory.filter(
      item => (item.quantity || 0) <= (item.minQuantity || 2) && (item.quantity || 0) > 0
    );
    const outOfStockItems = inventory.filter(item => (item.quantity || 0) <= 0);

    return {
      totalKinds,
      totalUnits,
      totalCost,
      totalSaleValue,
      potentialProfit,
      criticalCount: criticalItems.length,
      outOfStockCount: outOfStockItems.length,
    };
  }, [inventory]);

  // Filtered & Sorted items
  const filteredItems = useMemo(() => {
    return inventory
      .filter(item => {
        // Search term
        const searchLower = searchTerm.toLowerCase().trim();
        const matchesSearch =
          !searchLower ||
          item.name.toLowerCase().includes(searchLower) ||
          (item.code && item.code.toLowerCase().includes(searchLower)) ||
          (item.brand && item.brand.toLowerCase().includes(searchLower)) ||
          (item.model && item.model.toLowerCase().includes(searchLower)) ||
          (item.shelfLocation && item.shelfLocation.toLowerCase().includes(searchLower)) ||
          (item.supplier && item.supplier.toLowerCase().includes(searchLower)) ||
          (item.category && item.category.toLowerCase().includes(searchLower));

        // Category filter
        const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;

        // Stock status filter
        let matchesStatus = true;
        const qty = item.quantity || 0;
        const minQ = item.minQuantity || 2;
        if (stockStatusFilter === 'critical') {
          matchesStatus = qty <= minQ && qty > 0;
        } else if (stockStatusFilter === 'out_of_stock') {
          matchesStatus = qty <= 0;
        } else if (stockStatusFilter === 'in_stock') {
          matchesStatus = qty > minQ;
        }

        return matchesSearch && matchesCategory && matchesStatus;
      })
      .sort((a, b) => {
        if (sortBy === 'name_asc') {
          return a.name.localeCompare(b.name, 'tr', { sensitivity: 'base' });
        }
        if (sortBy === 'qty_asc') {
          return (a.quantity || 0) - (b.quantity || 0);
        }
        if (sortBy === 'qty_desc') {
          return (b.quantity || 0) - (a.quantity || 0);
        }
        if (sortBy === 'cost_desc') {
          return (b.costPrice || 0) - (a.costPrice || 0);
        }
        // date_desc
        return new Date(b.updatedAt || b.createdAt || 0).getTime() - new Date(a.updatedAt || a.createdAt || 0).getTime();
      });
  }, [inventory, searchTerm, selectedCategory, stockStatusFilter, sortBy]);

  // Reset page when filters or sorting change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedCategory, stockStatusFilter, sortBy]);

  // Pagination calculations
  const totalPages = Math.max(1, Math.ceil(filteredItems.length / pageSize));
  const safeCurrentPage = Math.min(Math.max(1, currentPage), totalPages);

  const paginatedItems = useMemo(() => {
    const startIndex = (safeCurrentPage - 1) * pageSize;
    return filteredItems.slice(startIndex, startIndex + pageSize);
  }, [filteredItems, safeCurrentPage, pageSize]);

  // Categories list from managed categories + existing inventory items
  const allCategories = useMemo(() => {
    const set = new Set<string>(categories && categories.length > 0 ? categories : defaultInventoryCategories);
    inventory.forEach(i => {
      if (i.category && i.category.trim()) set.add(i.category.trim());
    });
    return Array.from(set);
  }, [categories, inventory]);

  const handleSaveCategories = (newCats: string[]) => {
    if (onUpdateCategories) {
      onUpdateCategories(newCats);
    } else {
      saveInventoryCategories(newCats);
    }
  };

  // Handle Quick Adjust Quantity (+1 / -1)
  const handleQuickAdjust = (item: InventoryItem, delta: number) => {
    const prevQty = item.quantity || 0;
    if (delta < 0 && prevQty <= 0) {
      alert('Stok miktarı zaten 0 olduğu için stoktan düşülemez!');
      return;
    }
    const newQty = Math.max(0, prevQty + delta);
    if (newQty === prevQty) return;

    const nowISO = new Date().toISOString();
    let updatedItem: InventoryItem;

    if (delta > 0) {
      updatedItem = addInventoryLotPurchase(
        item,
        delta,
        item.costPrice || 0,
        item.currency || 'TRY',
        item.costPriceUSD,
        item.costExchangeRate || item.exchangeRate,
        item.supplier,
        'Hızlı Stok Artırımı (+1)'
      );
    } else {
      const res = consumeItemStockLowestCostFirst(item, Math.abs(delta));
      updatedItem = res.updatedItem;
    }

    const newMovement: StockMovement = {
      id: `SM-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
      itemId: item.id,
      itemName: item.name,
      itemCode: item.code,
      type: delta > 0 ? 'in' : 'out',
      quantity: Math.abs(delta),
      previousQuantity: prevQty,
      newQuantity: updatedItem.quantity,
      unitCost: item.costPrice,
      unitPrice: item.salePrice,
      note: delta > 0 ? 'Hızlı Stok Artırımı (+1)' : 'Hızlı Stok Azaltımı (-1)',
      date: nowISO,
    };

    const updatedInventory = inventory.map(i => (i.id === item.id ? updatedItem : i));
    const updatedMovements = [newMovement, ...stockMovements];

    onUpdateInventory(updatedInventory);
    onUpdateStockMovements(updatedMovements);
  };

  // Handle Save Item (Add or Edit)
  const handleSaveItem = (itemData: InventoryItem) => {
    const nowISO = new Date().toISOString();
    let updatedInventory: InventoryItem[];
    let updatedMovements = [...stockMovements];

    const isNew = !inventory.some(i => i.id === itemData.id);

    // Prevent duplicate stock codes
    const trimmedCode = (itemData.code || '').trim();
    if (trimmedCode) {
      const duplicate = inventory.find(
        i => i.id !== itemData.id &&
        ((i.code && i.code.trim().toLowerCase() === trimmedCode.toLowerCase()) ||
         i.id.trim().toLowerCase() === trimmedCode.toLowerCase())
      );
      if (duplicate) {
        alert(`Bu stok kodu ("${trimmedCode}") zaten "${duplicate.name}" (ID: ${duplicate.id}) adlı parçada kullanılmaktadır. Aynı stok kodu ile mükerrer ürün eklenemez!`);
        return;
      }
    }

    if (isNew) {
      const newItem: InventoryItem = {
        ...itemData,
        createdAt: itemData.createdAt || nowISO,
        updatedAt: nowISO,
      };

      // If item has quantity > 0 and no lots created yet, generate initial lot
      if (newItem.quantity > 0 && (!newItem.lots || newItem.lots.length === 0)) {
        newItem.lots = [
          {
            id: `LOT-${Date.now()}-init`,
            itemId: newItem.id,
            quantity: newItem.quantity,
            remainingQuantity: newItem.quantity,
            costPrice: newItem.costPrice || 0,
            costPriceUSD: newItem.costPriceUSD,
            exchangeRate: newItem.costExchangeRate || newItem.exchangeRate,
            currency: newItem.currency || 'TRY',
            purchaseDate: newItem.createdAt || nowISO,
            supplier: newItem.supplier,
            note: 'Başlangıç Stok Partisi'
          }
        ];
      }

      updatedInventory = [newItem, ...inventory];

      // Add initial stock movement if quantity > 0
      if (newItem.quantity > 0) {
        const movement: StockMovement = {
          id: `SM-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
          itemId: newItem.id,
          itemName: newItem.name,
          itemCode: newItem.code,
          type: 'in',
          quantity: newItem.quantity,
          previousQuantity: 0,
          newQuantity: newItem.quantity,
          unitCost: newItem.costPrice,
          unitPrice: newItem.salePrice,
          note: 'Yeni parça / ilk stok girişi',
          date: nowISO,
        };
        updatedMovements = [movement, ...updatedMovements];
      }
    } else {
      const existing = inventory.find(i => i.id === itemData.id);
      let updatedItem: InventoryItem = {
        ...itemData,
        updatedAt: nowISO,
      };

      // Check if quantity changed manually via edit modal
      if (existing && existing.quantity !== itemData.quantity) {
        const delta = itemData.quantity - existing.quantity;
        if (delta > 0) {
          updatedItem = addInventoryLotPurchase(
            existing,
            delta,
            itemData.costPrice || existing.costPrice || 0,
            itemData.currency || existing.currency || 'TRY',
            itemData.costPriceUSD,
            itemData.exchangeRate,
            itemData.supplier || existing.supplier,
            'Stok Kartı Düzenleme Artırımı'
          );
        } else if (delta < 0) {
          const res = consumeItemStockLowestCostFirst(existing, Math.abs(delta));
          updatedItem = res.updatedItem;
        }
        updatedItem.salePrice = itemData.salePrice;
        updatedItem.name = itemData.name;
        updatedItem.code = itemData.code;
        updatedItem.category = itemData.category;
        updatedItem.brand = itemData.brand;
        updatedItem.model = itemData.model;
        updatedItem.shelfLocation = itemData.shelfLocation;
        updatedItem.supplier = itemData.supplier;
        updatedItem.notes = itemData.notes;
        updatedItem.minQuantity = itemData.minQuantity;

        const movement: StockMovement = {
          id: `SM-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
          itemId: updatedItem.id,
          itemName: updatedItem.name,
          itemCode: updatedItem.code,
          type: 'adjustment',
          quantity: Math.abs(delta),
          previousQuantity: existing.quantity,
          newQuantity: updatedItem.quantity,
          unitCost: updatedItem.costPrice,
          unitPrice: updatedItem.salePrice,
          note: `Stok kartı düzenleme (${delta > 0 ? `+${delta}` : delta} adet düzeltme)`,
          date: nowISO,
        };
        updatedMovements = [movement, ...updatedMovements];
      }

      updatedInventory = inventory.map(i => (i.id === itemData.id ? updatedItem : i));
    }

    onUpdateInventory(updatedInventory);
    onUpdateStockMovements(updatedMovements);
    setIsItemModalOpen(false);
    setEditingItem(null);
  };

  // Handle Delete Item
  const handleDeleteItem = (id: string) => {
    if (isTechnician) {
      alert('Teknisyen hesaplarının stok silme yetkisi bulunmamaktadır.');
      return;
    }
    const target = inventory.find(i => i.id === id);
    if (!target) return;

    if (confirm(`"${target.name}" adlı stok parçasını silmek istediğinize emin misiniz?`)) {
      const updated = inventory.filter(i => i.id !== id);
      onUpdateInventory(updated);
    }
  };

  // Handle New Movement (from movement modal)
  const handleApplyMovement = (data: {
    itemId: string;
    type: 'in' | 'out' | 'adjustment';
    quantity: number;
    unitCost?: number;
    unitPrice?: number;
    currency?: CurrencyCode;
    unitCostUSD?: number;
    exchangeRate?: number;
    supplier?: string;
    note?: string;
  }) => {
    const item = inventory.find(i => i.id === data.itemId);
    if (!item) return;

    const prevQty = item.quantity || 0;
    const nowISO = new Date().toISOString();
    let updatedItem: InventoryItem;

    if (data.type === 'in') {
      const costTL = data.unitCost !== undefined && data.unitCost > 0 ? data.unitCost : (item.costPrice || 0);
      updatedItem = addInventoryLotPurchase(
        item,
        data.quantity,
        costTL,
        data.currency || item.currency || 'TRY',
        data.unitCostUSD,
        data.exchangeRate,
        data.supplier || item.supplier,
        data.note || 'Stok Alımı / Yeni Parti'
      );
      if (data.unitPrice !== undefined && data.unitPrice > 0) {
        updatedItem.salePrice = data.unitPrice;
      }
    } else if (data.type === 'out') {
      const res = consumeItemStockLowestCostFirst(item, data.quantity);
      updatedItem = res.updatedItem;
      if (data.unitPrice !== undefined && data.unitPrice > 0) {
        updatedItem.salePrice = data.unitPrice;
      }
    } else {
      // adjustment
      const delta = data.quantity - prevQty;
      if (delta < 0) {
        const res = consumeItemStockLowestCostFirst(item, Math.abs(delta));
        updatedItem = res.updatedItem;
      } else if (delta > 0) {
        updatedItem = addInventoryLotPurchase(
          item,
          delta,
          item.costPrice || 0,
          item.currency || 'TRY',
          undefined,
          undefined,
          item.supplier,
          'Sayım Düzeltmesi Artırımı'
        );
      } else {
        updatedItem = { ...item, updatedAt: nowISO };
      }
      if (data.unitPrice !== undefined && data.unitPrice > 0) {
        updatedItem.salePrice = data.unitPrice;
      }
    }

    const movement: StockMovement = {
      id: `SM-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
      itemId: item.id,
      itemName: item.name,
      itemCode: item.code,
      type: data.type,
      quantity: data.type === 'adjustment' ? Math.abs(updatedItem.quantity - prevQty) : data.quantity,
      previousQuantity: prevQty,
      newQuantity: updatedItem.quantity,
      unitCost: data.unitCost || item.costPrice,
      unitPrice: data.unitPrice || item.salePrice,
      note: data.note || (data.type === 'in' ? 'Mal alımı / Stok girişi' : data.type === 'out' ? 'Stok çıkışı / Satış' : 'Sayım düzeltmesi'),
      date: nowISO,
    };

    const updatedInventory = inventory.map(i => (i.id === item.id ? updatedItem : i));
    const updatedMovements = [movement, ...stockMovements];

    onUpdateInventory(updatedInventory);
    onUpdateStockMovements(updatedMovements);
    setIsMovementModalOpen(false);
    setMovementTargetItem(null);
  };

  return (
    <div className="space-y-6">
      {/* Top Banner / Hero Summary */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 space-y-4">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 border-b border-slate-100 pb-4">
          <div className="flex items-center space-x-3">
            <div className="w-11 h-11 rounded-xl bg-blue-50 text-blue-600 border border-blue-200 flex items-center justify-center shadow-xs shrink-0">
              <Package className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-lg font-black tracking-tight text-slate-900 flex items-center gap-2 flex-wrap">
                <span>Stok & Yedek Parça Takip Merkezi</span>
                {metrics.criticalCount > 0 && (
                  <span className="text-xs px-2.5 py-0.5 rounded-full font-bold bg-amber-100 text-amber-900 border border-amber-300 animate-pulse flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3 text-amber-600" />
                    {metrics.criticalCount} Kritik Stok
                  </span>
                )}
              </h2>
              <p className="text-xs text-slate-500 line-clamp-1 sm:line-clamp-none">
                Teknik serviste kullanılan ekran, batarya, entegre, soket ve tüm yedek parçaların anlık stok, raf ve maliyet takibi
              </p>
            </div>
          </div>

          {/* Top Actions */}
          <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap shrink-0">
            <button
              onClick={() => {
                setEditingItem(null);
                setIsItemModalOpen(true);
              }}
              className="px-3.5 py-2.5 bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold rounded-xl shadow-md shadow-blue-500/20 transition-all flex items-center gap-1.5 shrink-0 cursor-pointer whitespace-nowrap"
            >
              <Plus className="w-4 h-4" />
              <span>Yeni Parça Ekle</span>
            </button>

            <button
              onClick={() => setIsCategoriesModalOpen(true)}
              className="px-3 py-2.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-800 border border-indigo-200 text-xs font-bold rounded-xl transition-colors flex items-center gap-1.5 shrink-0 cursor-pointer shadow-xs whitespace-nowrap"
              title="Kategori Ekle / Sil / Düzenle"
            >
              <Tag className="w-3.5 h-3.5 text-indigo-600" />
              <span>Kategoriler</span>
            </button>

            <button
              onClick={() => {
                setMovementTargetItem(null);
                setIsMovementModalOpen(true);
              }}
              className="px-3 py-2.5 bg-slate-800 hover:bg-slate-700 text-white text-xs font-semibold rounded-xl shadow-sm transition-all flex items-center gap-1.5 shrink-0 cursor-pointer whitespace-nowrap"
              title="Mal alımı veya stok çıkışı yap"
            >
              <RefreshCw className="w-3.5 h-3.5 text-blue-400" />
              <span>Stok Hareketi</span>
            </button>

            <div className="flex items-center gap-1.5">
              <button
                onClick={() => exportInventoryExcel(inventory, stockMovements, companyInfo)}
                className="px-3 py-2.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 border border-emerald-300 text-xs font-bold rounded-xl transition-colors flex items-center gap-1.5 shrink-0 cursor-pointer whitespace-nowrap"
                title="Excel (.xlsx) olarak indir"
              >
                <FileSpreadsheet className="w-3.5 h-3.5 text-emerald-600" />
                <span>Excel</span>
              </button>

              <button
                onClick={() => exportInventoryPDF(inventory, companyInfo)}
                className="px-3 py-2.5 bg-rose-50 hover:bg-rose-100 text-rose-800 border border-rose-300 text-xs font-bold rounded-xl transition-colors flex items-center gap-1.5 shrink-0 cursor-pointer whitespace-nowrap"
                title="PDF Envanter Raporu al"
              >
                <FileText className="w-3.5 h-3.5 text-rose-600" />
                <span>PDF</span>
              </button>
            </div>
          </div>
        </div>

        {/* 6 Key Metric Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {/* 1. Çeşit */}
          <div className="bg-slate-50 border border-slate-200/80 rounded-xl p-3.5 flex flex-col justify-between">
            <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider block">Ürün Çeşidi</span>
            <div className="mt-1.5 flex items-baseline justify-between">
              <span className="text-xl font-black text-slate-900">{metrics.totalKinds}</span>
              <span className="text-[11px] text-slate-500 font-medium">Kalem</span>
            </div>
          </div>

          {/* 2. Toplam Adet */}
          <div className="bg-indigo-50/70 border border-indigo-200/80 rounded-xl p-3.5 flex flex-col justify-between">
            <span className="text-[11px] font-bold text-indigo-700 uppercase tracking-wider block">Toplam Stok</span>
            <div className="mt-1.5 flex items-baseline justify-between">
              <span className="text-xl font-black text-indigo-950">{metrics.totalUnits}</span>
              <span className="text-[11px] text-indigo-700 font-medium">Adet</span>
            </div>
          </div>

          {/* 3. Toplam Maliyet / Sermaye */}
          <div className="bg-amber-50/80 border border-amber-200 rounded-xl p-3.5 flex flex-col justify-between">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-bold text-amber-800 uppercase tracking-wider block">Stok Maliyeti</span>
              <span className="text-[10px] bg-amber-200/80 text-amber-900 px-1.5 py-0.2 rounded font-bold">Alış</span>
            </div>
            <div className="mt-1.5">
              <span className="text-base sm:text-lg xl:text-xl font-black text-amber-950 block leading-tight tracking-tight">{formatTL(metrics.totalCost)}</span>
            </div>
          </div>

          {/* 4. Satış Değeri / Potansiyel Ciro */}
          <div className="bg-emerald-50/80 border border-emerald-200 rounded-xl p-3.5 flex flex-col justify-between">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-bold text-emerald-800 uppercase tracking-wider block">Satış Değeri</span>
              <span className="text-[10px] bg-emerald-200/80 text-emerald-900 px-1.5 py-0.2 rounded font-bold">Ciro</span>
            </div>
            <div className="mt-1.5">
              <span className="text-base sm:text-lg xl:text-xl font-black text-emerald-950 block leading-tight tracking-tight">{formatTL(metrics.totalSaleValue)}</span>
            </div>
          </div>

          {/* 5. Kritik Stoklar */}
          <button
            onClick={() => setStockStatusFilter(stockStatusFilter === 'critical' ? 'all' : 'critical')}
            className={`text-left rounded-xl p-3.5 transition-all border cursor-pointer flex flex-col justify-between ${
              stockStatusFilter === 'critical'
                ? 'bg-amber-500 text-white border-amber-600 shadow-sm ring-2 ring-amber-400'
                : 'bg-amber-50/40 hover:bg-amber-50 border-amber-200 text-slate-800'
            }`}
          >
            <span className={`text-[11px] font-bold uppercase tracking-wider block ${stockStatusFilter === 'critical' ? 'text-amber-100' : 'text-amber-800'}`}>
              Kritik Stok
            </span>
            <div className="mt-1.5 flex items-baseline justify-between">
              <span className={`text-xl font-black ${stockStatusFilter === 'critical' ? 'text-white' : 'text-amber-900'}`}>
                {metrics.criticalCount}
              </span>
              <span className={`text-[11px] font-medium ${stockStatusFilter === 'critical' ? 'text-amber-100' : 'text-amber-700'}`}>
                Parça
              </span>
            </div>
          </button>

          {/* 6. Tükenenler */}
          <button
            onClick={() => setStockStatusFilter(stockStatusFilter === 'out_of_stock' ? 'all' : 'out_of_stock')}
            className={`text-left rounded-xl p-3.5 transition-all border cursor-pointer flex flex-col justify-between ${
              stockStatusFilter === 'out_of_stock'
                ? 'bg-rose-600 text-white border-rose-700 shadow-sm ring-2 ring-rose-400'
                : 'bg-rose-50/40 hover:bg-rose-50 border-rose-200 text-slate-800'
            }`}
          >
            <span className={`text-[11px] font-bold uppercase tracking-wider block ${stockStatusFilter === 'out_of_stock' ? 'text-rose-100' : 'text-rose-800'}`}>
              Tükenen
            </span>
            <div className="mt-1.5 flex items-baseline justify-between">
              <span className={`text-xl font-black ${stockStatusFilter === 'out_of_stock' ? 'text-white' : 'text-rose-900'}`}>
                {metrics.outOfStockCount}
              </span>
              <span className={`text-[11px] font-medium ${stockStatusFilter === 'out_of_stock' ? 'text-rose-100' : 'text-rose-700'}`}>
                0 Adet
              </span>
            </div>
          </button>
        </div>

        {/* Live TCMB USD/TRY Exchange Rate Widget */}
        <div className="bg-gradient-to-r from-emerald-50 via-teal-50 to-blue-50 p-3.5 rounded-xl border border-emerald-200/80 flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-2xs">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-emerald-600 text-white flex items-center justify-center font-bold text-sm shadow-xs shrink-0">
              <DollarSign className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-bold text-slate-800">
                  TCMB Günlük Dolar ($) Kuru:
                </span>
                <span className="text-sm font-black font-mono text-emerald-800 bg-white px-2 py-0.5 rounded-md border border-emerald-300 shadow-2xs">
                  1 USD = {exchangeRate.rate.toFixed(4)} ₺
                </span>
                <span className="text-[10px] font-bold text-emerald-700 bg-emerald-100/80 px-2 py-0.5 rounded-full border border-emerald-300 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                  {exchangeRate.source || 'TCMB Canlı'} • Saatlik Otomatik
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0 self-end sm:self-center">
            <span className="text-[10px] text-slate-400">
              Son Güncelleme: {formatDateTR(exchangeRate.updatedAt)}
            </span>
            <button
              type="button"
              onClick={() => handleRefreshRate(true)}
              disabled={isUpdatingRate}
              className="px-2.5 py-1.5 bg-white hover:bg-emerald-50 text-emerald-800 border border-emerald-300 rounded-lg text-xs font-bold transition-all flex items-center gap-1 cursor-pointer disabled:opacity-60 shadow-2xs"
              title="T.C. Merkez Bankası güncel kurunu sorgula"
            >
              <RefreshCw className={`w-3.5 h-3.5 text-emerald-600 ${isUpdatingRate ? 'animate-spin' : ''}`} />
              <span>{isUpdatingRate ? 'Çekiliyor...' : 'Kuru Yenile'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Mode Switcher Tabs: Stok Listesi vs Stok Hareketleri Geçmişi */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2 bg-slate-200 p-1 rounded-xl">
          <button
            onClick={() => setViewMode('list')}
            className={`px-4 py-1.5 text-xs font-bold rounded-lg transition-all flex items-center gap-1.5 cursor-pointer ${
              viewMode === 'list'
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <Layers className="w-3.5 h-3.5 text-blue-600" />
            <span>Yedek Parça & Ürün Envanteri ({filteredItems.length})</span>
          </button>

          <button
            onClick={() => setViewMode('movements')}
            className={`px-4 py-1.5 text-xs font-bold rounded-lg transition-all flex items-center gap-1.5 cursor-pointer ${
              viewMode === 'movements'
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <History className="w-3.5 h-3.5 text-indigo-600" />
            <span>Stok Hareketleri Günlüğü ({stockMovements.length})</span>
          </button>
        </div>
      </div>

      {viewMode === 'list' ? (
        <>
          {/* Search, Filter & Sorter Toolbar */}
          <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-center">
              {/* Search input */}
              <div className="md:col-span-5 relative">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Parça adı, stok kodu / barkod, marka/model, raf no veya tedarikçi ara..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-8 py-2 text-xs border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-0.5"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>

              {/* Category Filter */}
              <div className="md:col-span-3 flex items-center gap-1.5">
                <select
                  value={selectedCategory}
                  onChange={e => setSelectedCategory(e.target.value)}
                  className="w-full px-3 py-2 text-xs border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white font-medium text-slate-700"
                >
                  <option value="all">Tüm Kategoriler ({allCategories.length})</option>
                  {allCategories.map(cat => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => setIsCategoriesModalOpen(true)}
                  className="p-2 text-indigo-700 hover:text-indigo-900 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 rounded-lg transition-colors shrink-0 cursor-pointer"
                  title="Kategori Ekle / Sil / Düzenle"
                >
                  <Tag className="w-4 h-4" />
                </button>
              </div>

              {/* Status Filter */}
              <div className="md:col-span-2">
                <select
                  value={stockStatusFilter}
                  onChange={e => setStockStatusFilter(e.target.value as any)}
                  className="w-full px-3 py-2 text-xs border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white font-medium text-slate-700"
                >
                  <option value="all">Tüm Stok Durumları</option>
                  <option value="critical">⚠️ Kritik Stoklar (≤ Eşik)</option>
                  <option value="out_of_stock">🚫 Tükenenler (0 Adet)</option>
                  <option value="in_stock">✅ Yeterli Stoklar</option>
                </select>
              </div>

              {/* Sort selector */}
              <div className="md:col-span-2">
                <select
                  value={sortBy}
                  onChange={e => setSortBy(e.target.value as any)}
                  className="w-full px-3 py-2 text-xs border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white font-medium text-slate-700"
                >
                  <option value="name_asc">Parça Adı: A → Z (Alfabetik)</option>
                  <option value="date_desc">En Son Güncellenen</option>
                  <option value="qty_asc">Stok Miktarı: Azdan Çoğa</option>
                  <option value="qty_desc">Stok Miktarı: Çoktan Aza</option>
                  <option value="cost_desc">Maliyet: Yüksekten Düşüğe</option>
                </select>
              </div>
            </div>

            {/* Active filters bar if applied */}
            {(searchTerm || selectedCategory !== 'all' || stockStatusFilter !== 'all') && (
              <div className="flex items-center justify-between pt-2 border-t border-slate-100 text-xs">
                <div className="flex flex-wrap items-center gap-1.5 text-slate-600">
                  <span className="font-semibold text-slate-700">Filtrelenen Sonuç:</span>
                  <span className="font-bold text-blue-600">{filteredItems.length} parça listeleniyor</span>
                  {selectedCategory !== 'all' && (
                    <span className="bg-slate-100 text-slate-700 px-2 py-0.5 rounded-full font-medium border border-slate-200">
                      Kategori: {selectedCategory}
                    </span>
                  )}
                  {stockStatusFilter !== 'all' && (
                    <span className="bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full font-medium border border-amber-200">
                      Durum: {stockStatusFilter === 'critical' ? 'Kritik Stok' : stockStatusFilter === 'out_of_stock' ? 'Tükenen' : 'Yeterli'}
                    </span>
                  )}
                </div>
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setSelectedCategory('all');
                    setStockStatusFilter('all');
                  }}
                  className="text-slate-500 hover:text-rose-600 font-bold hover:underline"
                >
                  Filtreleri Temizle
                </button>
              </div>
            )}
          </div>

          {/* Inventory Table */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            {filteredItems.length > 0 ? (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs border-collapse">
                  <thead>
                    <tr className="bg-slate-50/90 text-slate-700 font-bold border-b border-slate-200 uppercase tracking-wider text-[11px]">
                      <th className="py-3 px-4">Kod & Parça Adı</th>
                      <th className="py-3 px-3">Kategori</th>
                      <th className="py-3 px-3">Uyumlu Model / Marka</th>
                      <th className="py-3 px-3">Raf / Konum</th>
                      <th className="py-3 px-3 text-right">Alış (Maliyet)</th>
                      <th className="py-3 px-3 text-right">Satış Fiyatı</th>
                      <th className="py-3 px-3 text-right">Birim Kâr</th>
                      <th className="py-3 px-4 text-center">Stok Durumu</th>
                      <th className="py-3 px-4 text-center">Hızlı Adet</th>
                      <th className="py-3 px-4 text-right">İşlemler</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {paginatedItems.map(item => {
                      const qty = item.quantity || 0;
                      const minQ = item.minQuantity || 2;
                      const isOutOfStock = qty <= 0;
                      const isCritical = qty <= minQ && !isOutOfStock;
                      const profit = (item.salePrice || 0) - (item.costPrice || 0);
                      const profitMargin = item.costPrice > 0 ? Math.round((profit / item.costPrice) * 100) : 0;

                      return (
                        <tr
                          key={item.id}
                          className={`hover:bg-slate-50/80 transition-colors ${
                            isOutOfStock ? 'bg-rose-50/20' : isCritical ? 'bg-amber-50/20' : ''
                          }`}
                        >
                          {/* Code & Name */}
                          <td className="py-3 px-4">
                            <div className="flex flex-col">
                              <div className="flex items-center gap-1.5">
                                <span className="font-mono text-[10px] font-bold bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded border border-slate-200">
                                  {item.code || item.id}
                                </span>
                                <span className="font-bold text-slate-900">{item.name}</span>
                              </div>
                              {item.notes && (
                                <span className="text-[10px] text-slate-400 mt-0.5 line-clamp-1 italic">
                                  {item.notes}
                                </span>
                              )}
                            </div>
                          </td>

                          {/* Category */}
                          <td className="py-3 px-3">
                            <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-100 text-slate-700 border border-slate-200">
                              {item.category || 'Genel'}
                            </span>
                          </td>

                          {/* Brand / Model */}
                          <td className="py-3 px-3 text-slate-600">
                            {[item.brand, item.model].filter(Boolean).join(' ') || (
                              <span className="text-slate-300">-</span>
                            )}
                          </td>

                          {/* Shelf Location */}
                          <td className="py-3 px-3">
                            {item.shelfLocation ? (
                              <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-700 bg-slate-100 px-2 py-0.5 rounded border border-slate-200">
                                <MapPin className="w-3 h-3 text-blue-500" />
                                {item.shelfLocation}
                              </span>
                            ) : (
                              <span className="text-slate-300">-</span>
                            )}
                          </td>

                          {/* Cost Price */}
                          <td className="py-3 px-3 text-right font-mono font-bold text-amber-800">
                            <div title="Sıradaki Çıkış Maliyeti">{formatTL(item.costPrice || 0)}</div>
                            {item.currency === 'USD' && item.costPriceUSD !== undefined && item.costPriceUSD > 0 ? (
                              <div className="text-[10px] text-amber-600 font-semibold flex items-center justify-end gap-0.5">
                                <span>{formatUSD(item.costPriceUSD)}</span>
                              </div>
                            ) : null}
                          </td>

                          {/* Sale Price */}
                          <td className="py-3 px-3 text-right font-mono font-bold text-emerald-800">
                            <div>{formatTL(item.salePrice || 0)}</div>
                            {item.currency === 'USD' && item.salePriceUSD !== undefined && item.salePriceUSD > 0 && (
                              <div className="text-[10px] text-emerald-600 font-semibold flex items-center justify-end gap-0.5">
                                <span>{formatUSD(item.salePriceUSD)}</span>
                              </div>
                            )}
                          </td>

                          {/* Profit */}
                          <td className="py-3 px-3 text-right">
                            <div className="flex flex-col items-end">
                              <span className={`font-mono font-bold text-[11px] ${profit >= 0 ? 'text-emerald-700' : 'text-rose-600'}`}>
                                {formatTL(profit)}
                              </span>
                              <span className="text-[10px] text-slate-400">
                                {profitMargin >= 0 ? `+${profitMargin}%` : `${profitMargin}%`}
                              </span>
                            </div>
                          </td>

                          {/* Stock Badge & Quantity */}
                          <td className="py-3 px-4 text-center">
                            {isOutOfStock ? (
                              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-black bg-rose-100 text-rose-800 border border-rose-300">
                                <XCircle className="w-3.5 h-3.5 text-rose-600" />
                                Tükendi (0)
                              </span>
                            ) : isCritical ? (
                              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold bg-amber-100 text-amber-900 border border-amber-300">
                                <AlertTriangle className="w-3.5 h-3.5 text-amber-600 animate-pulse" />
                                {qty} Adet (Kritik)
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold bg-emerald-100 text-emerald-900 border border-emerald-300">
                                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                                {qty} Adet
                              </span>
                            )}
                          </td>

                          {/* Quick Adjust Buttons */}
                          <td className="py-3 px-4 text-center">
                            <div className="inline-flex items-center space-x-1 bg-slate-100 p-0.5 rounded-lg border border-slate-200">
                              <button
                                onClick={() => handleQuickAdjust(item, -1)}
                                disabled={qty <= 0}
                                title="Stoktan 1 adet düş"
                                className="w-6 h-6 rounded flex items-center justify-center text-slate-600 hover:bg-rose-100 hover:text-rose-700 disabled:opacity-30 disabled:hover:bg-transparent cursor-pointer"
                              >
                                <MinusCircle className="w-3.5 h-3.5" />
                              </button>
                              <span className="w-8 text-center font-bold text-xs font-mono text-slate-900">
                                {qty}
                              </span>
                              <button
                                onClick={() => handleQuickAdjust(item, 1)}
                                title="Stoka 1 adet ekle"
                                className="w-6 h-6 rounded flex items-center justify-center text-slate-600 hover:bg-emerald-100 hover:text-emerald-700 cursor-pointer"
                              >
                                <PlusCircle className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </td>

                          {/* Actions */}
                          <td className="py-3 px-4 text-right">
                            <div className="flex items-center justify-end space-x-1">
                              <button
                                onClick={() => {
                                  setMovementTargetItem(item);
                                  setIsMovementModalOpen(true);
                                }}
                                title="Stok Giriş / Çıkış Hareketi Ekle"
                                className="p-1.5 text-slate-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors cursor-pointer"
                              >
                                <RefreshCw className="w-3.5 h-3.5" />
                              </button>

                              <button
                                onClick={() => {
                                  setLotsTargetItem(item);
                                  setIsLotsModalOpen(true);
                                }}
                                title="Alım Partileri ve Parti Maliyetleri (Lot Takibi)"
                                className="p-1.5 text-slate-600 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors cursor-pointer"
                              >
                                <Boxes className="w-3.5 h-3.5" />
                              </button>

                              <button
                                onClick={() => {
                                  setHistoryFilterItemId(item.id);
                                  setIsHistoryModalOpen(true);
                                }}
                                title="Bu Parçanın Stok Hareket Geçmişi"
                                className="p-1.5 text-slate-600 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors cursor-pointer"
                              >
                                <History className="w-3.5 h-3.5" />
                              </button>

                              <button
                                onClick={() => {
                                  setEditingItem(item);
                                  setIsItemModalOpen(true);
                                }}
                                title="Stok Kartını Düzenle"
                                className="p-1.5 text-slate-600 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors cursor-pointer"
                              >
                                <Edit2 className="w-3.5 h-3.5" />
                              </button>

                              {!isTechnician && (
                                <button
                                  onClick={() => handleDeleteItem(item.id)}
                                  title="Stoktan Sil"
                                  className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors cursor-pointer"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination Controls */}
              <div className="px-4 py-3 bg-slate-50 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs">
                <div className="flex items-center gap-2 text-slate-600 font-medium">
                  <span>
                    Toplam <strong className="text-slate-900">{filteredItems.length}</strong> parçadan{' '}
                    <strong className="text-slate-900">
                      {(safeCurrentPage - 1) * pageSize + 1} - {Math.min(safeCurrentPage * pageSize, filteredItems.length)}
                    </strong>{' '}
                    arası gösteriliyor
                  </span>
                  <span className="text-slate-300 hidden sm:inline">•</span>
                  <span className="text-[11px] bg-slate-200/80 text-slate-700 px-2 py-0.5 rounded font-semibold hidden sm:inline-block">
                    Sayfa {safeCurrentPage} / {totalPages} (20 satır/sayfa)
                  </span>
                </div>

                {totalPages > 1 && (
                  <div className="flex items-center gap-1 flex-wrap justify-center">
                    {/* First Page */}
                    <button
                      type="button"
                      disabled={safeCurrentPage === 1}
                      onClick={() => setCurrentPage(1)}
                      className="p-1.5 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
                      title="İlk Sayfa (Sayfa 1)"
                    >
                      <ChevronsLeft className="w-4 h-4" />
                    </button>

                    {/* Prev Page */}
                    <button
                      type="button"
                      disabled={safeCurrentPage === 1}
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      className="px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed font-medium transition-colors cursor-pointer flex items-center gap-1"
                      title="Önceki Sayfa"
                    >
                      <ChevronLeft className="w-3.5 h-3.5" />
                      <span>Önceki</span>
                    </button>

                    {/* Page Numbers */}
                    <div className="flex items-center gap-1 mx-1">
                      {Array.from({ length: totalPages }, (_, i) => i + 1)
                        .filter(p => {
                          if (totalPages <= 7) return true;
                          if (p === 1 || p === totalPages) return true;
                          return Math.abs(p - safeCurrentPage) <= 1;
                        })
                        .map((pageNum, idx, arr) => {
                          const prev = arr[idx - 1];
                          const isEllipsis = prev && pageNum - prev > 1;

                          return (
                            <React.Fragment key={pageNum}>
                              {isEllipsis && (
                                <span className="px-1 text-slate-400 font-bold">...</span>
                              )}
                              <button
                                type="button"
                                onClick={() => setCurrentPage(pageNum)}
                                className={`min-w-[32px] h-8 text-xs font-bold rounded-lg border transition-all cursor-pointer ${
                                  safeCurrentPage === pageNum
                                    ? 'bg-blue-600 text-white border-blue-600 shadow-xs'
                                    : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-100'
                                }`}
                              >
                                {pageNum}
                              </button>
                            </React.Fragment>
                          );
                        })}
                    </div>

                    {/* Next Page */}
                    <button
                      type="button"
                      disabled={safeCurrentPage === totalPages}
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      className="px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed font-medium transition-colors cursor-pointer flex items-center gap-1"
                      title="Sonraki Sayfa"
                    >
                      <span>Sonraki</span>
                      <ChevronRight className="w-3.5 h-3.5" />
                    </button>

                    {/* Last Page */}
                    <button
                      type="button"
                      disabled={safeCurrentPage === totalPages}
                      onClick={() => setCurrentPage(totalPages)}
                      className="p-1.5 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
                      title={`Son Sayfa (Sayfa ${totalPages})`}
                    >
                      <ChevronsRight className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
              </>
            ) : (
              <div className="p-12 text-center space-y-3">
                <div className="w-12 h-12 bg-slate-100 text-slate-400 rounded-full flex items-center justify-center mx-auto">
                  <Package className="w-6 h-6" />
                </div>
                <h4 className="text-sm font-bold text-slate-700">Kayıtlı Stok Parçası Bulunamadı</h4>
                <p className="text-xs text-slate-500 max-w-sm mx-auto">
                  Arama kriterlerinize uygun parça bulunamadı veya henüz stok kartı eklenmedi.
                </p>
                <button
                  onClick={() => {
                    setEditingItem(null);
                    setIsItemModalOpen(true);
                  }}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold rounded-xl shadow-sm inline-flex items-center gap-1.5"
                >
                  <Plus className="w-4 h-4" />
                  <span>İlk Parçayı Ekle</span>
                </button>
              </div>
            )}
          </div>
        </>
      ) : (
        /* Movements Tab */
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="p-4 border-b border-slate-100 flex items-center justify-between">
            <h3 className="font-bold text-sm text-slate-800 flex items-center gap-2">
              <History className="w-4 h-4 text-indigo-600" />
              <span>Tüm Stok Giriş, Çıkış ve Servis Kullanım Hareketleri</span>
            </h3>
            <span className="text-xs text-slate-500 font-semibold">{stockMovements.length} Hareket Kaydı</span>
          </div>

          {stockMovements.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs border-collapse">
                <thead>
                  <tr className="bg-slate-50 text-slate-700 font-bold border-b border-slate-200 uppercase tracking-wider text-[11px]">
                    <th className="py-3 px-4">Tarih & Saat</th>
                    <th className="py-3 px-3">İşlem Türü</th>
                    <th className="py-3 px-4">Ürün / Parça Adı</th>
                    <th className="py-3 px-3 text-center">Değişim</th>
                    <th className="py-3 px-3 text-center">Önceki / Yeni Stok</th>
                    <th className="py-3 px-3">İlgili Servis No</th>
                    <th className="py-3 px-4">Açıklama / Not</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {stockMovements.map(m => (
                    <tr key={m.id} className="hover:bg-slate-50/80 transition-colors">
                      <td className="py-3 px-4 text-slate-600 font-medium">
                        {formatDateTR(m.date)}
                      </td>
                      <td className="py-3 px-3">
                        {m.type === 'in' ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-300">
                            <ArrowDownRight className="w-3 h-3 text-emerald-600" />
                            Giriş (Alım)
                          </span>
                        ) : m.type === 'out' ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-rose-100 text-rose-800 border border-rose-300">
                            <ArrowUpRight className="w-3 h-3 text-rose-600" />
                            Çıkış (Servis/Satış)
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-purple-100 text-purple-800 border border-purple-300">
                            <RefreshCw className="w-3 h-3 text-purple-600" />
                            Sayım Düzeltmesi
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex flex-col">
                          <span className="font-bold text-slate-900">{m.itemName}</span>
                          {m.itemCode && <span className="text-[10px] text-slate-400 font-mono">{m.itemCode}</span>}
                        </div>
                      </td>
                      <td className="py-3 px-3 text-center">
                        <span
                          className={`font-mono font-bold text-xs ${
                            m.type === 'in'
                              ? 'text-emerald-700'
                              : m.type === 'out'
                              ? 'text-rose-600'
                              : 'text-purple-700'
                          }`}
                        >
                          {m.type === 'in' ? `+${m.quantity}` : m.type === 'out' ? `-${m.quantity}` : `${m.quantity}`} Adet
                        </span>
                      </td>
                      <td className="py-3 px-3 text-center font-mono text-slate-600 text-xs">
                        {m.previousQuantity} → <strong className="text-slate-900">{m.newQuantity}</strong>
                      </td>
                      <td className="py-3 px-3">
                        {m.ticketId ? (
                          <span className="inline-block font-mono font-bold text-blue-700 bg-blue-50 px-2 py-0.5 rounded border border-blue-200 text-[11px]">
                            #{m.ticketId}
                          </span>
                        ) : (
                          <span className="text-slate-300">-</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-slate-600 text-xs">
                        {m.note || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-12 text-center text-slate-400 italic text-xs">
              Henüz kaydedilmiş bir stok hareketi bulunmuyor.
            </div>
          )}
        </div>
      )}

      {/* 1. Item Add / Edit Modal */}
      {isItemModalOpen && (
        <InventoryItemModal
          isOpen={isItemModalOpen}
          initialItem={editingItem}
          categories={allCategories}
          existingItems={inventory}
          onClose={() => {
            setIsItemModalOpen(false);
            setEditingItem(null);
          }}
          onSave={handleSaveItem}
          onOpenManageCategories={() => setIsCategoriesModalOpen(true)}
        />
      )}

      {/* 2. Quick Movement Modal */}
      {isMovementModalOpen && (
        <QuickMovementModal
          isOpen={isMovementModalOpen}
          targetItem={movementTargetItem}
          allItems={inventory}
          onClose={() => {
            setIsMovementModalOpen(false);
            setMovementTargetItem(null);
          }}
          onApply={handleApplyMovement}
        />
      )}

      {/* 3. Single Item History Modal */}
      {isHistoryModalOpen && (
        <ItemHistoryModal
          isOpen={isHistoryModalOpen}
          itemId={historyFilterItemId}
          items={inventory}
          movements={stockMovements}
          onClose={() => {
            setIsHistoryModalOpen(false);
            setHistoryFilterItemId(null);
          }}
        />
      )}

      {/* 3.1. Item Lots & Batch Cost Modal */}
      {isLotsModalOpen && lotsTargetItem && (
        <ItemLotsModal
          isOpen={isLotsModalOpen}
          item={inventory.find(i => i.id === lotsTargetItem.id) || lotsTargetItem}
          onClose={() => {
            setIsLotsModalOpen(false);
            setLotsTargetItem(null);
          }}
          onAddNewLot={(item) => {
            setIsLotsModalOpen(false);
            setLotsTargetItem(null);
            setMovementTargetItem(item);
            setIsMovementModalOpen(true);
          }}
        />
      )}

      {/* 4. Manage Categories Modal */}
      {isCategoriesModalOpen && (
        <ManageInventoryCategoriesModal
          isOpen={isCategoriesModalOpen}
          categories={categories && categories.length > 0 ? categories : defaultInventoryCategories}
          inventory={inventory}
          onClose={() => setIsCategoriesModalOpen(false)}
          onSaveCategories={handleSaveCategories}
          onUpdateInventory={onUpdateInventory}
        />
      )}
    </div>
  );
};

// ==========================================
// SUB-MODAL 1: Add / Edit Inventory Item
// ==========================================
interface InventoryItemModalProps {
  isOpen: boolean;
  initialItem: InventoryItem | null;
  categories: string[];
  existingItems: InventoryItem[];
  onClose: () => void;
  onSave: (item: InventoryItem) => void;
  onOpenManageCategories?: () => void;
}

const InventoryItemModal: React.FC<InventoryItemModalProps> = ({
  isOpen,
  initialItem,
  categories,
  existingItems,
  onClose,
  onSave,
  onOpenManageCategories,
}) => {
  const isInitialUSD = initialItem?.currency === 'USD' || (initialItem?.costPriceUSD !== undefined && (initialItem.costPriceUSD || 0) > 0);
  const [currency, setCurrency] = useState<CurrencyCode>(isInitialUSD ? 'USD' : 'TRY');
  const [exchangeRate, setExchangeRate] = useState<ExchangeRateInfo>(() => getStoredExchangeRate());
  const [isUpdatingRate, setIsUpdatingRate] = useState(false);

  const [code, setCode] = useState(initialItem?.code || '');
  const [name, setName] = useState(initialItem?.name || '');
  const [category, setCategory] = useState(initialItem?.category || categories[0] || 'Genel');
  const [deviceArea, setDeviceArea] = useState<'' | 'printer' | 'computer'>(initialItem?.deviceArea || '');
  const [customCategory, setCustomCategory] = useState('');
  const [isAddingNewCat, setIsAddingNewCat] = useState(false);
  const [brand, setBrand] = useState(initialItem?.brand || '');
  const [model, setModel] = useState(initialItem?.model || '');
  const [quantityStr, setQuantityStr] = useState<string>(initialItem?.quantity !== undefined ? String(initialItem.quantity) : '1');
  const [minQuantityStr, setMinQuantityStr] = useState<string>(initialItem?.minQuantity !== undefined ? String(initialItem.minQuantity) : '2');
  
  // Cost & Sale inputs based on currency
  const [costPriceStr, setCostPriceStr] = useState<string>(() => {
    if (isInitialUSD && initialItem?.costPriceUSD !== undefined) {
      return String(initialItem.costPriceUSD);
    }
    return initialItem?.costPrice !== undefined ? String(initialItem.costPrice) : '0';
  });
  const [salePriceStr, setSalePriceStr] = useState<string>(() => {
    if (isInitialUSD && initialItem?.salePriceUSD !== undefined) {
      return String(initialItem.salePriceUSD);
    }
    return initialItem?.salePrice !== undefined ? String(initialItem.salePrice) : '0';
  });
  
  const [shelfLocation, setShelfLocation] = useState(initialItem?.shelfLocation || '');
  const [supplier, setSupplier] = useState(initialItem?.supplier || '');
  const [notes, setNotes] = useState(initialItem?.notes || '');
  const [errorMsg, setErrorMsg] = useState('');

  const trimmedCode = code.trim();
  const duplicateItem = useMemo(() => {
    if (!trimmedCode) return null;
    return existingItems.find(
      i =>
        i.id !== initialItem?.id &&
        ((i.code && i.code.trim().toLowerCase() === trimmedCode.toLowerCase()) ||
         i.id.trim().toLowerCase() === trimmedCode.toLowerCase())
    ) || null;
  }, [trimmedCode, existingItems, initialItem]);

  const parsedCost = parseFloat(costPriceStr) || 0;
  const parsedSale = parseFloat(salePriceStr) || 0;
  
  // Converted and calculated financial values
  const activeRate = exchangeRate.rate || 47.95;
  const costTL = currency === 'USD' ? convertUSDToTL(parsedCost, activeRate) : parsedCost;
  const saleTL = currency === 'USD' ? convertUSDToTL(parsedSale, activeRate) : parsedSale;
  const profitTL = saleTL - costTL;
  const profitUSD = currency === 'USD' ? parsedSale - parsedCost : (parsedSale - parsedCost) / activeRate;
  const margin = (currency === 'USD' ? parsedCost : costTL) > 0 ? Math.round((profitTL / costTL) * 100) : 0;

  const handleCurrencyChange = (newCurr: CurrencyCode) => {
    if (newCurr === currency) return;
    if (newCurr === 'USD') {
      // Convert current TL input to USD
      const newCostUSD = parsedCost > 0 ? (parsedCost / activeRate).toFixed(2) : '0';
      const newSaleUSD = parsedSale > 0 ? (parsedSale / activeRate).toFixed(2) : '0';
      setCostPriceStr(newCostUSD);
      setSalePriceStr(newSaleUSD);
    } else {
      // Convert current USD input to TL
      const newCostTL = parsedCost > 0 ? (parsedCost * activeRate).toFixed(2) : '0';
      const newSaleTL = parsedSale > 0 ? (parsedSale * activeRate).toFixed(2) : '0';
      setCostPriceStr(newCostTL);
      setSalePriceStr(newSaleTL);
    }
    setCurrency(newCurr);
  };

  const handleRefreshRateInModal = async () => {
    setIsUpdatingRate(true);
    try {
      const freshRate = await fetchLiveExchangeRate();
      setExchangeRate(freshRate);
    } catch (err) {
      console.error(err);
    } finally {
      setIsUpdatingRate(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg('');

    if (!name.trim()) {
      setErrorMsg('Lütfen parça / ürün adını giriniz.');
      return;
    }

    if (duplicateItem) {
      setErrorMsg(`Bu stok kodu ("${trimmedCode}") zaten "${duplicateItem.name}" (#${duplicateItem.id}) adlı parçada kullanılmaktadır. Aynı stok kodu ile mükerrer ürün eklenemez!`);
      return;
    }

    const finalCategory = isAddingNewCat && customCategory.trim() ? customCategory.trim() : category;

    const parsedQty = parseInt(quantityStr, 10);
    const finalQty = isNaN(parsedQty) ? 0 : Math.max(0, parsedQty);

    const parsedMinQty = parseInt(minQuantityStr, 10);
    const finalMinQty = isNaN(parsedMinQty) ? 0 : Math.max(0, parsedMinQty);

    const id = initialItem?.id || generateNextInventoryId(existingItems);
    const itemData: InventoryItem = {
      id,
      code: code.trim() || id,
      name: name.trim(),
      category: finalCategory,
      deviceArea: deviceArea || undefined,
      brand: brand.trim() || undefined,
      model: model.trim() || undefined,
      quantity: finalQty,
      minQuantity: finalMinQty,
      costPrice: Math.max(0, costTL),
      salePrice: Math.max(0, saleTL),
      currency: currency,
      costPriceUSD: currency === 'USD' ? Math.max(0, parsedCost) : undefined,
      salePriceUSD: currency === 'USD' ? Math.max(0, parsedSale) : undefined,
      exchangeRate: currency === 'USD' ? activeRate : undefined,
      costExchangeRate: currency === 'USD' ? (initialItem?.costExchangeRate || activeRate) : undefined,
      saleExchangeRate: currency === 'USD' ? activeRate : undefined,
      shelfLocation: shelfLocation.trim() || undefined,
      supplier: supplier.trim() || undefined,
      notes: notes.trim() || undefined,
      createdAt: initialItem?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    onSave(itemData);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-900/70 backdrop-blur-xs animate-in fade-in duration-150">
      <div className="min-h-full flex items-center justify-center p-3 sm:p-4 text-center">
        <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col border border-slate-200 overflow-hidden my-auto text-left">
        {/* Modal Header */}
        <div className="p-4 bg-slate-900 text-white flex items-center justify-between border-b border-slate-800">
          <div className="flex items-center space-x-2.5">
            <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center">
              <Package className="w-4 h-4 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-sm">
                {initialItem ? 'Stok Kartını Düzenle' : 'Yeni Stok / Yedek Parça Ekle'}
              </h3>
              <p className="text-[11px] text-slate-400">
                {initialItem ? `#${initialItem.id} - ${initialItem.name}` : 'Envantere yeni yedek parça kaydı açın'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white p-1 rounded-lg transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Body Form */}
        <form onSubmit={handleSubmit} className="p-5 overflow-y-auto space-y-4 flex-1 text-xs">
          {errorMsg && (
            <div className="p-3 bg-rose-50 border border-rose-200 text-rose-700 rounded-xl font-medium flex items-center gap-2">
              <AlertCircle className="w-4 h-4 shrink-0 text-rose-600" />
              <span>{errorMsg}</span>
            </div>
          )}

          {/* Code & Name Row */}
          <div className="grid grid-cols-1 sm:grid-cols-12 gap-3">
            <div className="sm:col-span-4">
              <label className="block text-slate-700 font-bold mb-1">
                Stok Kodu / Barkod
              </label>
              <div className="relative">
                <Barcode className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Örn: EKR-IPH11"
                  value={code}
                  onChange={e => {
                    setCode(e.target.value);
                    if (errorMsg) setErrorMsg('');
                  }}
                  className={`w-full pl-8 pr-3 py-2 border rounded-lg text-xs font-mono font-bold focus:ring-2 transition-colors ${
                    duplicateItem
                      ? 'border-rose-400 bg-rose-50/40 text-rose-900 focus:ring-rose-500/20 focus:border-rose-500'
                      : 'border-slate-300 focus:ring-blue-500/20 focus:border-blue-500'
                  }`}
                />
              </div>
              {duplicateItem && (
                <div className="mt-1 flex items-start gap-1 text-[11px] text-rose-600 font-bold leading-tight animate-in fade-in duration-150">
                  <AlertCircle className="w-3.5 h-3.5 text-rose-600 shrink-0 mt-0.5" />
                  <span>Bu stok kodu zaten "<strong>{duplicateItem.name}</strong>" ürününde kayıtlı! Tekrar eklenemez.</span>
                </div>
              )}
            </div>

            <div className="sm:col-span-8">
              <label className="block text-slate-700 font-bold mb-1">
                Parça / Ürün Adı <span className="text-rose-500">*</span>
              </label>
              <input
                type="text"
                required
                placeholder="Örn: iPhone 11 GX OLED Ekran"
                value={name}
                onChange={e => setName(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-xs font-bold focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Category, Brand, Model Row */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-slate-700 font-bold">Kategori</label>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setIsAddingNewCat(!isAddingNewCat)}
                    className="text-[10px] font-bold text-blue-600 hover:underline cursor-pointer"
                  >
                    {isAddingNewCat ? 'Listeden Seç' : '+ Hızlı Ekle'}
                  </button>
                  {onOpenManageCategories && (
                    <button
                      type="button"
                      onClick={onOpenManageCategories}
                      className="text-[10px] font-bold text-indigo-600 hover:underline cursor-pointer"
                    >
                      Yönet
                    </button>
                  )}
                </div>
              </div>
              {isAddingNewCat ? (
                <input
                  type="text"
                  placeholder="Yeni kategori adı..."
                  value={customCategory}
                  onChange={e => setCustomCategory(e.target.value)}
                  className="w-full px-3 py-2 border border-blue-300 rounded-lg text-xs font-medium focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-blue-50/30"
                />
              ) : (
                <select
                  value={category}
                  onChange={e => setCategory(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-xs font-medium focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white"
                >
                  {categories.map(c => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              )}
            </div>

            <div>
              <label className="block text-slate-700 font-bold mb-1">Marka</label>
              <input
                type="text"
                placeholder="Apple, Samsung, Universal vb."
                value={brand}
                onChange={e => setBrand(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-slate-700 font-bold mb-1">Uyumlu Model</label>
              <input
                type="text"
                placeholder="iPhone 11, Galaxy S20 vb."
                value={model}
                onChange={e => setModel(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              />
            </div>
          </div>

          <div className="rounded-xl border border-blue-200 bg-blue-50/60 p-3">
            <label className="block text-slate-800 font-bold mb-1">Aylık rapor stok alanı</label>
            <select
              value={deviceArea}
              onChange={event => setDeviceArea(event.target.value as '' | 'printer' | 'computer')}
              className="w-full sm:max-w-sm px-3 py-2 border border-blue-300 rounded-lg bg-white text-xs font-semibold text-slate-800"
            >
              <option value="">Otomatik (stok adı / servis kullanımı)</option>
              <option value="printer">Yazıcı / Tarayıcı stoğu</option>
              <option value="computer">Bilgisayar stoğu</option>
            </select>
            <p className="mt-1 text-[10px] text-slate-600">Servis fişinde henüz kullanılmamış yazıcı parçalarının da Yazıcı Aylık stok toplamında görünmesi için Yazıcı / Tarayıcı stoğunu seçin.</p>
          </div>

          {/* Quantities & Critical Alert */}
          <div className="bg-slate-50 p-3.5 rounded-xl border border-slate-200 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-slate-700 font-bold mb-1">
                Mevcut Stok Miktarı (Adet) <span className="text-rose-500">*</span>
              </label>
              <input
                type="text"
                inputMode="numeric"
                required
                value={quantityStr}
                onChange={e => setQuantityStr(e.target.value.replace(/[^0-9]/g, ''))}
                placeholder="0"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm font-black font-mono text-slate-900 bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-slate-700 font-bold mb-1">
                Kritik Stok Uyarısı Eşiği (Adet)
              </label>
              <input
                type="text"
                inputMode="numeric"
                value={minQuantityStr}
                onChange={e => setMinQuantityStr(e.target.value.replace(/[^0-9]/g, ''))}
                placeholder="2"
                className="w-full px-3 py-2 border border-amber-300 rounded-lg text-sm font-bold font-mono text-amber-900 bg-amber-50/50 focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
              />
              <span className="text-[10px] text-slate-500 mt-0.5 block">
                Bu adedin altına düştüğünde sarı kritik uyarısı verir.
              </span>
            </div>
          </div>

          {/* Pricing Box (Cost, Sale & Profit Preview with Multi-Currency) */}
          <div className="bg-emerald-50/40 p-3.5 rounded-xl border border-emerald-200/80 space-y-3">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-emerald-200/60 pb-2">
              <div>
                <span className="text-[11px] font-bold text-emerald-900 block uppercase tracking-wider">
                  Fiyatlandırma & Kâr Hesabı
                </span>
                <span className="text-[10px] text-slate-500">
                  {currency === 'USD' ? 'Dolar ($) ile girilen değerler TCMB kuruyla otomatik TL’ye çevrilir.' : 'Türk Lirası (₺) üzerinden doğrudan hesaplama'}
                </span>
              </div>

              {/* Currency Selector Toggle */}
              <div className="flex items-center bg-white p-0.5 rounded-lg border border-emerald-300 shrink-0 shadow-2xs">
                <button
                  type="button"
                  onClick={() => handleCurrencyChange('TRY')}
                  className={`px-2.5 py-1 text-xs font-bold rounded-md transition-all cursor-pointer ${
                    currency === 'TRY'
                      ? 'bg-emerald-600 text-white shadow-xs'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  ₺ Türk Lirası (TRY)
                </button>
                <button
                  type="button"
                  onClick={() => handleCurrencyChange('USD')}
                  className={`px-2.5 py-1 text-xs font-bold rounded-md transition-all flex items-center gap-1 cursor-pointer ${
                    currency === 'USD'
                      ? 'bg-blue-600 text-white shadow-xs'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  <DollarSign className="w-3 h-3" />
                  <span>$ Dolar (USD)</span>
                </button>
              </div>
            </div>

            {/* If USD: Live TCMB Rate Info Banner inside Modal */}
            {currency === 'USD' && (
              <div className="bg-blue-50/80 p-2.5 rounded-lg border border-blue-200 flex items-center justify-between gap-2 text-xs">
                <div className="flex items-center gap-2">
                  <span className="font-bold text-blue-950">Günlük TCMB Kuru:</span>
                  <span className="font-mono font-black text-blue-800 bg-white px-2 py-0.5 rounded border border-blue-300">
                    1 USD = {activeRate.toFixed(4)} ₺
                  </span>
                  <span className="text-[10px] text-blue-600">({exchangeRate.source || 'TCMB Canlı'})</span>
                </div>
                <button
                  type="button"
                  onClick={handleRefreshRateInModal}
                  disabled={isUpdatingRate}
                  className="text-[11px] font-bold text-blue-700 hover:underline flex items-center gap-1 cursor-pointer"
                >
                  <RefreshCw className={`w-3 h-3 ${isUpdatingRate ? 'animate-spin' : ''}`} />
                  <span>{isUpdatingRate ? 'Çekiliyor...' : 'Kuru Yenile'}</span>
                </button>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-slate-700 font-bold mb-1">
                  Alış / Maliyet {currency === 'USD' ? '($ USD)' : '(₺ TRY)'}
                </label>
                <div className="relative">
                  <input
                    type="text"
                    inputMode="decimal"
                    placeholder="0.00"
                    value={costPriceStr}
                    onChange={e => setCostPriceStr(e.target.value.replace(/[^0-9.]/g, ''))}
                    className="w-full px-3 py-2 border border-amber-300 bg-white rounded-lg text-xs font-bold font-mono text-amber-900 focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
                  />
                  {currency === 'USD' && (
                    <span className="text-[10px] text-slate-400 absolute right-2.5 top-1/2 -translate-y-1/2 font-bold">$ USD</span>
                  )}
                </div>
                {currency === 'USD' && (
                  <span className="text-[10px] text-amber-800 font-semibold mt-1 block">
                    TL Karşılığı: <b>{formatTL(costTL)}</b>
                  </span>
                )}
              </div>

              <div>
                <label className="block text-slate-700 font-bold mb-1">
                  Satış Fiyatı {currency === 'USD' ? '($ USD)' : '(₺ TRY)'}
                </label>
                <div className="relative">
                  <input
                    type="text"
                    inputMode="decimal"
                    placeholder="0.00"
                    value={salePriceStr}
                    onChange={e => setSalePriceStr(e.target.value.replace(/[^0-9.]/g, ''))}
                    className="w-full px-3 py-2 border border-emerald-300 bg-white rounded-lg text-xs font-bold font-mono text-emerald-900 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                  />
                  {currency === 'USD' && (
                    <span className="text-[10px] text-slate-400 absolute right-2.5 top-1/2 -translate-y-1/2 font-bold">$ USD</span>
                  )}
                </div>
                {currency === 'USD' && (
                  <span className="text-[10px] text-emerald-800 font-semibold mt-1 block">
                    TL Karşılığı: <b>{formatTL(saleTL)}</b>
                  </span>
                )}
              </div>

              <div className="flex flex-col justify-center bg-white p-2.5 rounded-lg border border-emerald-200 shadow-2xs">
                <span className="text-[10px] text-slate-500 font-semibold">Birim Kâr & Marj:</span>
                <div className="flex items-baseline justify-between mt-0.5">
                  <div className="flex flex-col">
                    <span className={`font-bold font-mono text-xs ${profitTL >= 0 ? 'text-emerald-700' : 'text-rose-600'}`}>
                      {formatTL(profitTL)}
                    </span>
                    {currency === 'USD' && (
                      <span className="text-[10px] text-blue-700 font-mono font-semibold">
                        {formatUSD(profitUSD)}
                      </span>
                    )}
                  </div>
                  <span className="text-[10px] font-bold text-slate-600">
                    {margin >= 0 ? `+${margin}%` : `${margin}%`}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Shelf Location & Supplier */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-slate-700 font-bold mb-1">
                Raf / Çekmece / Kutu Konumu
              </label>
              <input
                type="text"
                placeholder="Örn: Raf A-3, Kutu 12, Dolap 2"
                value={shelfLocation}
                onChange={e => setShelfLocation(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-slate-700 font-bold mb-1">
                Tedarikçi / Toptancı Firma
              </label>
              <input
                type="text"
                placeholder="Örn: Eksen İletişim, Deji vb."
                value={supplier}
                onChange={e => setSupplier(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-slate-700 font-bold mb-1">Açıklama / Notlar</label>
            <input
              type="text"
              placeholder="Örn: Orijinal revize, 6 ay garantili parça..."
              value={notes}
              onChange={e => setNotes(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
            />
          </div>

          {/* Modal Footer Actions */}
          <div className="pt-3 border-t border-slate-200 flex items-center justify-end space-x-2.5">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl transition-colors cursor-pointer"
            >
              Vazgeç
            </button>
            <button
              type="submit"
              className="px-5 py-2 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-xl shadow-md shadow-blue-500/20 transition-all cursor-pointer"
            >
              {initialItem ? 'Değişiklikleri Kaydet' : 'Stok Kartını Ekle'}
            </button>
          </div>
        </form>
      </div>
      </div>
    </div>
  );
};

// ==========================================
// SUB-MODAL 2: Quick Movement (Stok Giriş / Çıkış & Lot Alımı)
// ==========================================
interface QuickMovementModalProps {
  isOpen: boolean;
  targetItem: InventoryItem | null;
  allItems: InventoryItem[];
  onClose: () => void;
  onApply: (data: {
    itemId: string;
    type: 'in' | 'out' | 'adjustment';
    quantity: number;
    unitCost?: number;
    unitPrice?: number;
    currency?: CurrencyCode;
    unitCostUSD?: number;
    exchangeRate?: number;
    supplier?: string;
    note?: string;
  }) => void;
}

const QuickMovementModal: React.FC<QuickMovementModalProps> = ({
  isOpen,
  targetItem,
  allItems,
  onClose,
  onApply,
}) => {
  const [selectedItemId, setSelectedItemId] = useState<string>(targetItem?.id || allItems[0]?.id || '');
  const [movementType, setMovementType] = useState<'in' | 'out' | 'adjustment'>('in');
  const [quantityStr, setQuantityStr] = useState<string>('1');
  const [currency, setCurrency] = useState<CurrencyCode>(targetItem?.currency || 'TRY');
  const [unitCostStr, setUnitCostStr] = useState<string>(targetItem ? String(targetItem.costPrice || '') : '');
  const [unitCostUSDStr, setUnitCostUSDStr] = useState<string>(targetItem?.costPriceUSD ? String(targetItem.costPriceUSD) : '');
  const [unitPriceStr, setUnitPriceStr] = useState<string>(targetItem ? String(targetItem.salePrice || '') : '');
  const [supplier, setSupplier] = useState<string>(targetItem?.supplier || '');
  const [note, setNote] = useState<string>('');
  const [errorMsg, setErrorMsg] = useState<string>('');

  const currentItem = allItems.find(i => i.id === selectedItemId);
  const currentQty = currentItem?.quantity || 0;

  // Sync cost/price when item changes
  React.useEffect(() => {
    if (currentItem) {
      setCurrency(currentItem.currency || 'TRY');
      setUnitCostStr(currentItem.costPrice !== undefined ? String(currentItem.costPrice) : '');
      setUnitCostUSDStr(currentItem.costPriceUSD !== undefined ? String(currentItem.costPriceUSD) : '');
      setUnitPriceStr(currentItem.salePrice !== undefined ? String(currentItem.salePrice) : '');
      setSupplier(currentItem.supplier || '');
    }
  }, [selectedItemId]);

  const parsedQty = parseInt(quantityStr, 10);
  const validQty = isNaN(parsedQty) ? 0 : parsedQty;
  const parsedCost = parseFloat(unitCostStr) || 0;
  const parsedPrice = parseFloat(unitPriceStr) || 0;
  const parsedCostUSD = parseFloat(unitCostUSDStr) || undefined;

  const isOutOfStockForDeduction = movementType === 'out' && currentQty <= 0;
  const isExceedingStock = movementType === 'out' && validQty > currentQty;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg('');

    if (!selectedItemId) {
      setErrorMsg('Lütfen işlem yapılacak parçayı seçiniz.');
      return;
    }

    if (validQty <= 0) {
      setErrorMsg('Lütfen 1 veya daha büyük bir miktar giriniz.');
      return;
    }

    if (movementType === 'out' && currentQty <= 0) {
      setErrorMsg('Bu parçanın stok miktarı 0 olduğu için stoktan düşülemez!');
      return;
    }

    if (movementType === 'out' && validQty > currentQty) {
      setErrorMsg(`Yetersiz stok! Mevcut stok (${currentQty} adet) miktarından fazla çıkış yapılamaz.`);
      return;
    }

    onApply({
      itemId: selectedItemId,
      type: movementType,
      quantity: validQty,
      unitCost: movementType === 'in' ? parsedCost : undefined,
      unitPrice: (movementType === 'in' || movementType === 'out') && parsedPrice > 0 ? parsedPrice : undefined,
      currency: movementType === 'in' ? currency : undefined,
      unitCostUSD: movementType === 'in' ? parsedCostUSD : undefined,
      supplier: movementType === 'in' ? supplier.trim() || undefined : undefined,
      note: note.trim() || undefined,
    });
  };

  const previewNewQty = currentItem
    ? movementType === 'in'
      ? currentQty + validQty
      : movementType === 'out'
      ? Math.max(0, currentQty - validQty)
      : validQty
    : 0;

  // Multi-lot valuation simulation for incoming purchase
  const activeLots = currentItem ? getItemActiveLots(currentItem) : [];
  const currentTotalVal = currentItem ? calculateItemTotalInventoryValue(currentItem) : 0;
  const newLotTotalVal = validQty * (parsedCost > 0 ? parsedCost : (currentItem?.costPrice || 0));
  const simulatedTotalVal = currentTotalVal + newLotTotalVal;
  const simulatedWeightedAvg = previewNewQty > 0 ? simulatedTotalVal / previewNewQty : 0;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-900/70 backdrop-blur-xs animate-in fade-in duration-150">
      <div className="min-h-full flex items-center justify-center p-3 sm:p-4 text-center">
        <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full border border-slate-200 overflow-hidden my-auto text-left">
          <div className="p-4 bg-slate-900 text-white flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <RefreshCw className="w-4 h-4 text-blue-400" />
              <h3 className="font-bold text-sm">Hızlı Stok Hareketi & Parti Alımı</h3>
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-white p-1 rounded-lg cursor-pointer">
              <X className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-5 space-y-4 text-xs">
            {errorMsg && (
              <div className="p-2.5 bg-rose-50 border border-rose-200 text-rose-700 rounded-xl font-bold flex items-center gap-1.5">
                <AlertCircle className="w-4 h-4 shrink-0 text-rose-600" />
                <span>{errorMsg}</span>
              </div>
            )}

            {/* Select Item */}
            <div>
              <label className="block text-slate-700 font-bold mb-1">İşlem Yapılacak Parça</label>
              <select
                value={selectedItemId}
                onChange={e => {
                  setSelectedItemId(e.target.value);
                  setErrorMsg('');
                }}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-xs font-bold bg-white text-slate-800 focus:ring-2 focus:ring-blue-500"
              >
                {allItems.map(item => (
                  <option key={item.id} value={item.id}>
                    {item.name} ({item.code || item.id}) - Stok: {item.quantity} Adet (Maliyet: {formatTL(item.costPrice || 0)})
                  </option>
                ))}
              </select>
            </div>

            {/* Movement Type Radio / Buttons */}
            <div>
              <label className="block text-slate-700 font-bold mb-1.5">Hareket Türü</label>
              <div className="grid grid-cols-3 gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setMovementType('in');
                    setErrorMsg('');
                  }}
                  className={`py-2 px-2 rounded-xl font-bold border transition-all text-center cursor-pointer ${
                    movementType === 'in'
                      ? 'bg-emerald-600 text-white border-emerald-700 shadow-sm'
                      : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'
                  }`}
                >
                  + Stok Girişi (Alım)
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setMovementType('out');
                    setErrorMsg('');
                  }}
                  className={`py-2 px-2 rounded-xl font-bold border transition-all text-center cursor-pointer ${
                    movementType === 'out'
                      ? 'bg-rose-600 text-white border-rose-700 shadow-sm'
                      : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'
                  }`}
                >
                  - Stok Çıkışı (Satış)
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setMovementType('adjustment');
                    setErrorMsg('');
                  }}
                  className={`py-2 px-2 rounded-xl font-bold border transition-all text-center cursor-pointer ${
                    movementType === 'adjustment'
                      ? 'bg-purple-600 text-white border-purple-700 shadow-sm'
                      : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'
                  }`}
                >
                  Sayım Düzeltmesi
                </button>
              </div>
            </div>

            {/* Warning if stock is 0 and trying to deduct */}
            {isOutOfStockForDeduction && (
              <div className="p-2.5 bg-rose-50 border border-rose-300 text-rose-800 rounded-xl text-xs font-semibold flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-rose-600 shrink-0" />
                <span>Bu parçanın stok miktarı 0 adet olduğu için stok çıkışı yapılamaz.</span>
              </div>
            )}

            {isExceedingStock && !isOutOfStockForDeduction && (
              <div className="p-2.5 bg-amber-50 border border-amber-300 text-amber-800 rounded-xl text-xs font-semibold flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                <span>Mevcut stok ({currentQty} adet) miktarından fazla çıkış yapılamaz.</span>
              </div>
            )}

            {/* Quantity & Stock preview */}
            <div className="bg-slate-50 p-3 rounded-xl border border-slate-200 space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-slate-700 font-bold">
                  {movementType === 'adjustment' ? 'Yeni Net Sayım Miktarı (Adet):' : 'Hareket Miktarı (Adet):'}
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  required
                  value={quantityStr}
                  onChange={e => {
                    setQuantityStr(e.target.value.replace(/[^0-9]/g, ''));
                    setErrorMsg('');
                  }}
                  placeholder="1"
                  className="w-24 px-2.5 py-1.5 border border-slate-300 rounded-lg text-sm font-black font-mono text-center bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                />
              </div>

              {currentItem && (
                <div className="pt-2 border-t border-slate-200 flex items-center justify-between text-xs font-semibold">
                  <span className="text-slate-500">Mevcut Stok: {currentQty} Adet</span>
                  <span className="text-slate-800">
                    Sonuç: <strong className="text-blue-700 text-sm font-black">{previewNewQty} Adet</strong>
                  </span>
                </div>
              )}
            </div>

            {/* Inflow Lot Cost Fields */}
            {movementType === 'in' && (
              <div className="p-3.5 bg-amber-50/70 rounded-xl border border-amber-200/80 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-amber-900 flex items-center gap-1.5">
                    <Boxes className="w-4 h-4 text-amber-700" />
                    Yeni Parti Alış & Maliyet Bilgisi
                  </span>
                  <span className="text-[11px] font-semibold text-amber-700 bg-amber-100/80 px-2 py-0.5 rounded border border-amber-300">
                    Lot / Parti Ayrıştırmalı
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-slate-700 font-bold mb-1">
                      Bu Partinin Birim Alış Fiyatı (₺)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={unitCostStr}
                      onChange={e => setUnitCostStr(e.target.value)}
                      placeholder="0.00"
                      className="w-full px-2.5 py-1.5 border border-amber-300 rounded-lg font-mono font-bold bg-white text-slate-900 focus:ring-2 focus:ring-amber-500 focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-slate-700 font-bold mb-1">
                      Güncel Satış Fiyatı (₺) <span className="text-[10px] text-slate-400 font-normal">(opsiyonel)</span>
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={unitPriceStr}
                      onChange={e => setUnitPriceStr(e.target.value)}
                      placeholder="0.00"
                      className="w-full px-2.5 py-1.5 border border-slate-300 rounded-lg font-mono font-bold bg-white text-slate-900 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-slate-700 font-bold mb-1">Tedarikçi Firma</label>
                  <input
                    type="text"
                    placeholder="Örn: Özkanlar Otomotiv / İthalatçı X"
                    value={supplier}
                    onChange={e => setSupplier(e.target.value)}
                    className="w-full px-2.5 py-1.5 border border-slate-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  />
                </div>

                {/* Simulation Breakdown Card */}
                {currentItem && validQty > 0 && (
                  <div className="p-2.5 bg-white rounded-lg border border-amber-200 text-[11px] space-y-1 font-medium text-slate-700">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Mevcut Kalan Stok:</span>
                      <span className="font-bold">{currentQty} Adet ({formatTL(currentItem.costPrice || 0)} / ad.)</span>
                    </div>
                    <div className="flex justify-between text-emerald-700">
                      <span>Yeni Eklenecek Parti:</span>
                      <span className="font-bold">+{validQty} Adet ({formatTL(parsedCost > 0 ? parsedCost : (currentItem.costPrice || 0))} / ad.)</span>
                    </div>
                    <div className="pt-1.5 border-t border-slate-200 flex justify-between font-bold text-slate-900">
                      <span>Toplam Stok Değeri:</span>
                      <span className="font-mono text-amber-800">{formatTL(simulatedTotalVal)}</span>
                    </div>
                    <div className="flex justify-between font-bold text-slate-900">
                      <span>Ağırlıklı Ortalama Maliyet:</span>
                      <span className="font-mono text-blue-700">{formatTL(simulatedWeightedAvg)} / Adet</span>
                    </div>
                    <div className="pt-1 text-[10px] text-slate-500 italic">
                      * Servis kayıtlarında önce en eski parti (FIFO) maliyetinden düşülür; maliyetler birbirini ezmez.
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Optional Note */}
            <div>
              <label className="block text-slate-700 font-bold mb-1">Açıklama / Fatura / İrsaliye No</label>
              <input
                type="text"
                placeholder="Örn: Fatura No: 2026-8899, Toptan alım"
                value={note}
                onChange={e => setNote(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-500 focus:outline-none"
              />
            </div>

            {/* Footer */}
            <div className="pt-2 flex items-center justify-end space-x-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl cursor-pointer"
              >
                Vazgeç
              </button>
              <button
                type="submit"
                disabled={isOutOfStockForDeduction || isExceedingStock}
                className="px-5 py-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold rounded-xl shadow-md cursor-pointer"
              >
                Hareketi Uygula
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// ==========================================
// SUB-MODAL 3: Item History Modal
// ==========================================
interface ItemHistoryModalProps {
  isOpen: boolean;
  itemId: string | null;
  items: InventoryItem[];
  movements: StockMovement[];
  onClose: () => void;
}

const ItemHistoryModal: React.FC<ItemHistoryModalProps> = ({
  isOpen,
  itemId,
  items,
  movements,
  onClose,
}) => {
  const item = items.find(i => i.id === itemId);
  const itemMovements = movements.filter(m => m.itemId === itemId);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-900/70 backdrop-blur-xs animate-in fade-in duration-150">
      <div className="min-h-full flex items-center justify-center p-3 sm:p-4 text-center">
        <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[85vh] flex flex-col border border-slate-200 overflow-hidden my-auto text-left">
          <div className="p-4 bg-slate-900 text-white flex items-center justify-between">
            <div className="flex items-center space-x-2.5">
              <History className="w-5 h-5 text-indigo-400" />
              <div>
                <h3 className="font-bold text-sm">Parça Stok Hareket Geçmişi</h3>
                <p className="text-[11px] text-slate-400">
                  {item ? `${item.name} (${item.code || item.id})` : 'Tüm Hareketler'}
                </p>
              </div>
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-white p-1 rounded-lg cursor-pointer">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-4 overflow-y-auto flex-1 text-xs">
            {itemMovements.length > 0 ? (
              <div className="divide-y divide-slate-100">
                {itemMovements.map(m => (
                  <div key={m.id} className="py-2.5 flex items-center justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <span
                          className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                            m.type === 'in'
                              ? 'bg-emerald-100 text-emerald-800'
                              : m.type === 'out'
                              ? 'bg-rose-100 text-rose-800'
                              : 'bg-purple-100 text-purple-800'
                          }`}
                        >
                          {m.type === 'in' ? 'Giriş (+)' : m.type === 'out' ? 'Çıkış (-)' : 'Düzeltme'}
                        </span>
                        <span className="font-bold text-slate-800">{m.note || 'İşlem'}</span>
                        {m.ticketId && (
                          <span className="font-mono text-[10px] text-blue-600 bg-blue-50 px-1.5 py-0.2 rounded border border-blue-200">
                            #{m.ticketId}
                          </span>
                        )}
                      </div>
                      <span className="text-[11px] text-slate-400 mt-0.5 block">
                        {formatDateTR(m.date)}
                      </span>
                    </div>

                    <div className="text-right shrink-0">
                      <span
                        className={`font-mono font-bold text-xs block ${
                          m.type === 'in'
                            ? 'text-emerald-700'
                            : m.type === 'out'
                            ? 'text-rose-600'
                            : 'text-purple-700'
                        }`}
                      >
                        {m.type === 'in' ? `+${m.quantity}` : m.type === 'out' ? `-${m.quantity}` : m.quantity} Adet
                      </span>
                      <span className="text-[10px] text-slate-500 font-mono">
                        Stok: {m.previousQuantity} → <strong>{m.newQuantity}</strong>
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-8 text-center text-slate-400 italic">
                Bu parçaya ait henüz kayıtlı stok hareketi bulunmuyor.
              </div>
            )}
          </div>

          <div className="p-3 bg-slate-50 border-t border-slate-200 text-right">
            <button
              onClick={onClose}
              className="px-4 py-1.5 bg-slate-800 hover:bg-slate-700 text-white font-bold rounded-lg text-xs cursor-pointer"
            >
              Kapat
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==========================================
// SUB-MODAL 3.1: Item Lots & Batch Cost Modal
// ==========================================
interface ItemLotsModalProps {
  isOpen: boolean;
  item: InventoryItem;
  onClose: () => void;
  onAddNewLot?: (item: InventoryItem) => void;
}

const ItemLotsModal: React.FC<ItemLotsModalProps> = ({
  isOpen,
  item,
  onClose,
  onAddNewLot,
}) => {
  const lots = item.lots || [];
  const activeLots = lots.filter(l => (l.remainingQuantity ?? l.quantity) > 0);
  const totalStock = item.quantity || 0;
  const totalVal = calculateItemTotalInventoryValue(item);
  const weightedAvg = calculateItemWeightedAvgCost(item);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-900/70 backdrop-blur-xs animate-in fade-in duration-150">
      <div className="min-h-full flex items-center justify-center p-3 sm:p-4 text-center">
        <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[85vh] flex flex-col border border-slate-200 overflow-hidden my-auto text-left">
          {/* Header */}
          <div className="p-4 bg-slate-900 text-white flex items-center justify-between">
            <div className="flex items-center space-x-2.5">
              <Boxes className="w-5 h-5 text-amber-400" />
              <div>
                <h3 className="font-bold text-sm">Alım Partileri & Lot Maliyet Takibi</h3>
                <p className="text-[11px] text-slate-400">
                  {item.name} ({item.code || item.id})
                </p>
              </div>
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-white p-1 rounded-lg cursor-pointer">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Top Summary Card */}
          <div className="p-4 bg-amber-50/60 border-b border-amber-200/80">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
              <div className="bg-white p-2.5 rounded-xl border border-amber-200 shadow-2xs">
                <span className="text-[10px] text-slate-500 block">Mevcut Toplam Stok</span>
                <span className="text-sm font-black font-mono text-slate-900">{totalStock} Adet</span>
              </div>
              <div className="bg-white p-2.5 rounded-xl border border-amber-200 shadow-2xs">
                <span className="text-[10px] text-slate-500 block">Toplam Envanter Değeri</span>
                <span className="text-sm font-black font-mono text-amber-800">{formatTL(totalVal)}</span>
              </div>
              <div className="bg-white p-2.5 rounded-xl border border-amber-200 shadow-2xs">
                <span className="text-[10px] text-slate-500 block">Ağırlıklı Ortalama Maliyet</span>
                <span className="text-sm font-black font-mono text-blue-700">{formatTL(weightedAvg)}</span>
              </div>
              <div className="bg-white p-2.5 rounded-xl border border-amber-200 shadow-2xs">
                <span className="text-[10px] text-slate-500 block">Sıradaki Çıkış Maliyeti (FIFO)</span>
                <span className="text-sm font-black font-mono text-emerald-700">{formatTL(item.costPrice || 0)}</span>
              </div>
            </div>
          </div>

          {/* Lots List */}
          <div className="p-4 overflow-y-auto flex-1 text-xs">
            <div className="flex items-center justify-between mb-3">
              <span className="font-bold text-slate-800 text-xs">Kayıtlı Alım Partileri ({lots.length})</span>
              {onAddNewLot && (
                <button
                  onClick={() => onAddNewLot(item)}
                  className="inline-flex items-center gap-1 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-lg text-xs cursor-pointer shadow-2xs"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Yeni Alım Partisi Ekle
                </button>
              )}
            </div>

            {lots.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs border-collapse">
                  <thead>
                    <tr className="bg-slate-50 text-slate-600 font-bold border-b border-slate-200 uppercase tracking-wider text-[10px]">
                      <th className="py-2.5 px-3">Parti / Lot</th>
                      <th className="py-2.5 px-3">Alım Tarihi</th>
                      <th className="py-2.5 px-3 text-center">Başlangıç</th>
                      <th className="py-2.5 px-3 text-center">Kalan Stok</th>
                      <th className="py-2.5 px-3 text-right">Birim Maliyet</th>
                      <th className="py-2.5 px-3 text-right">Kalan Değer</th>
                      <th className="py-2.5 px-3">Tedarikçi / Not</th>
                      <th className="py-2.5 px-3 text-center">Durum</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {lots.map((lot, idx) => {
                      const rem = lot.remainingQuantity !== undefined ? lot.remainingQuantity : lot.quantity;
                      const isDepleted = rem <= 0;
                      const costTL = lot.costPrice || 0;
                      const remVal = rem * costTL;

                      return (
                        <tr
                          key={lot.id || idx}
                          className={`hover:bg-slate-50/80 transition-colors ${
                            isDepleted ? 'bg-slate-50/50 opacity-60' : 'bg-white'
                          }`}
                        >
                          <td className="py-2.5 px-3 font-mono font-bold text-slate-800">
                            #{idx + 1}
                            <span className="text-[10px] text-slate-400 block font-normal">{lot.id}</span>
                          </td>
                          <td className="py-2.5 px-3 text-slate-600">
                            {formatDateTR(lot.purchaseDate)}
                          </td>
                          <td className="py-2.5 px-3 text-center font-mono font-bold text-slate-700">
                            {lot.quantity} Adet
                          </td>
                          <td className="py-2.5 px-3 text-center">
                            <span
                              className={`font-mono font-black px-2 py-0.5 rounded-full text-[11px] ${
                                rem > 0
                                  ? 'bg-emerald-100 text-emerald-800 border border-emerald-300'
                                  : 'bg-slate-100 text-slate-600 border border-slate-300'
                              }`}
                            >
                              {rem} Adet
                            </span>
                          </td>
                          <td className="py-2.5 px-3 text-right font-mono font-bold text-amber-800">
                            {formatTL(costTL)}
                            {lot.currency === 'USD' && lot.costPriceUSD && (
                              <span className="block text-[10px] text-amber-600">
                                {formatUSD(lot.costPriceUSD)}
                              </span>
                            )}
                          </td>
                          <td className="py-2.5 px-3 text-right font-mono font-bold text-slate-900">
                            {formatTL(remVal)}
                          </td>
                          <td className="py-2.5 px-3 text-slate-600">
                            <div>{lot.supplier || '-'}</div>
                            {lot.note && <span className="text-[10px] text-slate-400 italic block">{lot.note}</span>}
                          </td>
                          <td className="py-2.5 px-3 text-center">
                            {rem > 0 ? (
                              <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                Aktif
                              </span>
                            ) : (
                              <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-500 border border-slate-200">
                                Tükendi
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-8 text-center text-slate-400 italic bg-slate-50 rounded-xl border border-slate-200">
                Bu parçaya ait henüz ayrı bir alım partisi kaydedilmemiş. İlk alım veya stok hareketi yapıldığında otomatik olarak partiler oluşturulur.
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-3 bg-slate-50 border-t border-slate-200 text-right">
            <button
              onClick={onClose}
              className="px-4 py-1.5 bg-slate-800 hover:bg-slate-700 text-white font-bold rounded-lg text-xs cursor-pointer"
            >
              Kapat
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
